import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { useErrors } from "../../contexts/ErrorContext";

interface Props {
  children: ReactNode;
  onError: (error: unknown) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React error boundary that forwards caught errors into Sentry and the
 * ErrorContext, then resets so the app can continue rendering.
 *
 * Ported from Captain's Log.
 */
class ErrorBoundaryClass extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack ?? "",
        },
      },
    });

    this.props.onError({
      ...error,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Reset after a tick so the app tree can continue rendering.
      setTimeout(() => {
        this.setState({ hasError: false, error: null });
      }, 100);
      return null;
    }
    return this.props.children;
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const { addError } = useErrors();
  return <ErrorBoundaryClass onError={addError}>{children}</ErrorBoundaryClass>;
}
